import { useEffect, useState } from "react";
import { Share, Download, X } from "lucide-react";
import { Button } from "@/components/ui/Button";

// Banner "Instalar o app" (PWA / Add to Home Screen):
// - Android/Chrome: intercepta o evento `beforeinstallprompt` e mostra um
//   banner próprio; o botão dispara o prompt nativo de instalação.
// - iOS/Safari: não existe prompt programático — mostramos instruções
//   (Compartilhar → Adicionar à Tela de Início).
// Dispensado = não insistir por 14 dias (localStorage). Nada aparece quando
// o app já está rodando instalado (display-mode: standalone).

const DISMISS_KEY = "bolao-pga/install-dismissed-at";
const DISMISS_DAYS = 14;

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

function isStandalone(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    // Safari iOS legado
    (navigator as { standalone?: boolean }).standalone === true
  );
}

function isIOS(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

function recentlyDismissed(): boolean {
  const at = Number(localStorage.getItem(DISMISS_KEY) ?? 0);
  return Date.now() - at < DISMISS_DAYS * 24 * 60 * 60 * 1000;
}

export function InstallPrompt() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(
    null,
  );
  const [showIOSHelp, setShowIOSHelp] = useState(false);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return;

    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setDeferred(null);
      setShowIOSHelp(false);
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);

    // iOS nunca dispara beforeinstallprompt; oferece as instruções direto.
    if (isIOS()) setShowIOSHelp(true);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setHidden(true);
  };

  const install = async () => {
    if (!deferred) return;
    await deferred.prompt();
    const choice = await deferred.userChoice;
    setDeferred(null);
    if (choice.outcome === "dismissed") dismiss();
    else setHidden(true);
  };

  if (hidden || (!deferred && !showIOSHelp)) return null;

  return (
    <div className="fixed inset-x-0 bottom-20 z-30 px-4">
      <div className="mx-auto flex max-w-lg items-center gap-3 rounded-(--radius-app) border border-border bg-surface-alt p-4 shadow-lg">
        <img
          src="/icons/icon-192.png"
          alt=""
          className="h-11 w-11 shrink-0 rounded-(--radius-field)"
        />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold">Instalar o Bolão PGA</p>
          {deferred ? (
            <p className="text-xs text-muted-foreground">
              Acesso rápido direto da sua tela inicial.
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              Toque em <Share size={12} className="inline align-[-2px]" />{" "}
              Compartilhar e depois em{" "}
              <span className="font-semibold text-foreground">
                Adicionar à Tela de Início
              </span>
              .
            </p>
          )}
        </div>
        {deferred && (
          <Button
            title="Instalar"
            icon={<Download size={14} />}
            onClick={() => void install()}
            className="h-9 shrink-0 px-3 text-xs"
          />
        )}
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dispensar"
          className="shrink-0 p-1 text-muted-foreground transition-colors hover:text-foreground"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
